/**
 * IntegrationCard — P3, the ONE integration card anatomy, instanced for
 * Google Ads / WordPress / Google Docs so the convention is learned once
 * and trusted everywhere (DOET conventions).
 *
 * The anatomy, top to bottom, never more:
 *   1. StatusPill top-right — the ONLY state signifier. No title icons,
 *      tags, or alerts that could disagree with it.
 *   2. ONE factual line — carries the cascade-bottleneck attribution
 *      ("Account mapping required" when connected-but-unmapped) and, for
 *      connected-but-degraded, the warning glyph + the error via the
 *      `degradedWarning` prop. Degraded is a warning affordance atop
 *      Connected — never a 4th pill state, never the unverified modifier
 *      (that one means fetch-staleness only).
 *   3. Read-only scope line — the full anti-affordance recipe, identical
 *      across cards: plain muted text + lock glyph, NO handlers, NO
 *      hover/pointer/button chrome — it points to where the action lives;
 *      it is never a way there.
 *   4. Exactly ONE primary action (bg-coral) when — and only when — there
 *      is something to do in this scope (Connect / Map account / Fix). A
 *      broken upstream disables it WITH the upstream reason rendered beside
 *      it — the true model surfaces exactly when the assumption breaks.
 *   5. Secondary actions only demoted and significantly different (small
 *      muted text buttons, never sibling primaries). Destructive ones go
 *      through DisconnectLockInDialog, which NAMES the blast radius.
 *   6. On-demand detail behind one quiet toggle — for Ads, the cascade in
 *      dependency order Plan → Connection → Mapping → Management tier.
 *
 * Strings come from copyTokens/StatusPill; factual-line content is data.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, ExternalLink, Lock } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StatusPill } from './StatusPill';
import { COPY } from '@/lib/copyTokens';
import type { CapabilityState } from '@/hooks/useClientCapabilities';

export interface IntegrationCardAction {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  /** Upstream reason rendered WITH the disabled action (never silent). */
  disabledReason?: string;
}

/**
 * The ONE pointer out of the card's scope — rendered as a SubordinateLink
 * immediately after the action's reason text. Passed only when the pointer
 * belongs to this viewer (e.g. elevated-only); never a button, never coral.
 */
export interface IntegrationCardScopeAction {
  label: string;
  to: string;
}

export interface IntegrationCardSecondaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface IntegrationCardProps {
  title: string;
  state: CapabilityState;
  /** Pass the capability's verified flag (WP: never a bare "Connected"). */
  verified?: boolean;
  /** Shared stale/failure vocabulary from staleSuffixFor. */
  staleSuffix?: string;
  /** The ONE factual line (data — may carry bottleneck attribution). */
  factualLine: ReactNode;
  /** connected+last_error → warning glyph + the error; pill stays Connected. */
  degradedWarning?: string;
  /** Read-only scope note (anti-affordance recipe; plain text, no chrome). */
  scopeLine: string;
  /** At most ONE — present only when there is something to do here. */
  primaryAction?: IntegrationCardAction;
  /** The ONE pointer out of scope, adjacent to the action's reason. */
  scopeAction?: IntegrationCardScopeAction;
  /** Demoted + significantly different (small muted text buttons). */
  secondaryActions?: IntegrationCardSecondaryAction[];
  /** On-demand detail (Ads: the cascade in dependency order). */
  detail?: ReactNode;
  testId?: string;
}

export function IntegrationCard({
  title,
  state,
  verified,
  staleSuffix,
  factualLine,
  degradedWarning,
  scopeLine,
  primaryAction,
  scopeAction,
  secondaryActions,
  detail,
  testId,
}: IntegrationCardProps) {
  const [detailOpen, setDetailOpen] = useState(false);

  return (
    <section
      data-testid={testId ?? `integration-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className="rounded-md border border-warm-border bg-paper p-4 shadow-soft"
    >
      <header className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{title}</h3>
        <StatusPill state={state} verified={verified} staleSuffix={staleSuffix} className="shrink-0" />
      </header>

      <p className="mt-1 text-sm text-ink-2">{factualLine}</p>

      {degradedWarning ? (
        <p className="mt-1 flex items-center gap-1 text-xs text-amber-dark">
          <AlertTriangle aria-hidden="true" className="size-3.5" />
          {degradedWarning}
        </p>
      ) : null}

      {/* Read-only scope line — the anti-affordance recipe: plain text,
          muted, lock glyph, no handlers, no cursor, no hover, no chrome. */}
      <p className="mt-1 flex items-center gap-1 text-xs text-ink-3">
        <Lock aria-hidden="true" className="size-3" />
        {scopeLine}
      </p>

      {primaryAction || (secondaryActions && secondaryActions.length > 0) ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {primaryAction ? (
            <span className="inline-flex flex-col gap-1">
              <button
                type="button"
                onClick={() => {
                  // The one action always lands on the thing it names: when
                  // the card has a detail, acting opens it ("Map account"
                  // takes you to the mapping form, not just a label).
                  if (detail) setDetailOpen(true);
                  primaryAction.onClick();
                }}
                disabled={primaryAction.disabled || primaryAction.loading}
                className="inline-flex items-center rounded-md bg-coral px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-coral-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {primaryAction.loading ? `${primaryAction.label}…` : primaryAction.label}
              </button>
              {primaryAction.disabled && primaryAction.disabledReason ? (
                <span className="text-xs text-ink-3">{primaryAction.disabledReason}</span>
              ) : null}
              {scopeAction ? (
                <span className="py-1">
                  <SubordinateLink to={scopeAction.to}>{scopeAction.label}</SubordinateLink>
                </span>
              ) : null}
            </span>
          ) : null}
          {secondaryActions?.map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              className="text-xs text-ink-3 underline decoration-warm-border-strong underline-offset-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-60"
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}

      {detail ? (
        <>
          <DisclosureToggle
            open={detailOpen}
            onToggle={() => setDetailOpen((open) => !open)}
            className="mt-3"
          >
            {detailOpen ? COPY.cards.detailsHide : COPY.cards.detailsShow}
          </DisclosureToggle>
          {detailOpen ? (
            <div className="mt-2 border-t border-warm-border pt-3">{detail}</div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/**
 * SubordinateLink — the ONE way a card or cascade points somewhere without
 * becoming an action: small, underlined, ink-2 (interactive but quiet),
 * significantly different from the coral primary and never a sibling of it.
 * Used for admin-gated and ops link-outs (Manage, Weekly management).
 */
export function SubordinateLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 text-xs font-medium text-ink-2 underline decoration-warm-border-strong underline-offset-2 hover:text-ink"
    >
      {children}
      {/* ↗ = leaves this scope. Semantic, never decorative (aria-hidden). */}
      <ExternalLink aria-hidden="true" className="size-3" />
    </Link>
  );
}

/**
 * DisclosureToggle — the ONE disclosure affordance across the settings surface
 * (GA-S5 PP-1). A quiet text toggle in the SAME register as before (xs,
 * font-medium, ink-2 → ink on hover) plus a rotating chevron: ChevronRight
 * points right when closed and rotates 90° (down) when open, driven by
 * aria-expanded. The chevron is the affordance glyph of the disclosure register
 * — a separate register from action icons (e.g. Detect's search glyph). Site
 * layout (mt-3, self-start) is passed via className; the label is children.
 */
export function DisclosureToggle({
  open,
  onToggle,
  className,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={[
        'inline-flex items-center gap-1 text-xs font-medium text-ink-2 hover:text-ink',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ChevronRight
        aria-hidden="true"
        className={`size-3.5 transition-transform${open ? ' rotate-90' : ''}`}
      />
      {children}
    </button>
  );
}

/**
 * DisconnectLockInDialog — the forcing function behind every destructive
 * integration action. It NAMES the blast radius: agency-level disconnects
 * say ALL workspaces; per-workspace ones name the exact thing that breaks.
 * One primary confirm (bg-coral), one quiet cancel — no lookalike siblings.
 */
export function DisconnectLockInDialog({
  open,
  onOpenChange,
  title,
  blastRadius,
  onConfirm,
  busy,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  blastRadius: string;
  onConfirm: () => void;
  busy?: boolean;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{blastRadius}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{COPY.cards.cancel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={busy}
            className="bg-coral text-primary-foreground hover:bg-coral-hover"
          >
            {busy ? `${COPY.cards.disconnect}…` : COPY.cards.disconnect}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
